import "../assets/css/vendor-product-forms.css";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Alert,
  Card,
  Form,
  Button,
  Container,
  Row,
  Col,
  Modal,
  Spinner,
} from "react-bootstrap";
import DiscountFields, { discountPayloadOf } from "../components/DiscountFields";
import { useProductFormGate } from "../components/team/Gated";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Switch, FormControlLabel } from "@material-ui/core";
import { AppAsyncSelect as AsyncSelect } from "../components/ui/AppReactSelect";
import Select from "../components/ui/AppReactSelect";
import CategorySuggestions from "../components/catalog/CategorySuggestions";
import { categoryPickerOptions, unitSelectOptions } from "../utilities/catalogTaxonomy";
import { uploadImageToCloudinary as sharedUpload } from "../utilities/imageUpload";
import { toast } from "react-toastify";
import {
  fetchHealthcareProduct,
  updateHealthcareProduct,
  deleteHealthcareProduct,
} from "../actions/healthcareProductActions";
import {
  fetchHealthcareSections,
  fetchHealthcareVendors,
} from "../actions/healthcareSectionActions";
import { getSiUnits } from "actions/siUnitActions";
import { getUserFromLocalStorage } from "../utilities/user";
import MobileProductShootPicker from "../components/MobileProductShootPicker";
import AdditionalImagesStrip from "../components/AdditionalImagesStrip";
import ProductMediaDeleteDialog from "../components/ProductMediaDeleteDialog";
import ProductVideoSection from "../components/ProductVideoSection";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import UploadProgress from "../components/UploadProgress";
import AppSelect from "../components/ui/AppSelect";
import { vendorOwnerIdOf } from "../utilities/vendorScope";

const AUDIT_ENABLED = process.env.REACT_APP_FEATURE_AUDIT_TRAIL === "true";

const useStyles = makeStyles((theme) => ({
  label: {
    fontWeight: "normal",
    fontSize: "12px",
    textTransform: "capitalize",
  },
}));

const EditHealthcareProductForm = () => {
  const classes = useStyles();
  const { productId } = useParams();
  const user = getUserFromLocalStorage();
  const vendorId = vendorOwnerIdOf(user);
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  const sections = useSelector((state) => state.healthcareSections.sectionsHealthcare);
  const healthcareVendors = useSelector((state) => state.healthcareSections.healthcareVendors);
  const { siUnits } = useSelector((state) => state.siUnits);

  const [product, setProduct] = useState({
    name: "",
    description: "",
    section: "",
    healthcareVendor: "",
    price: 0,
    /* "Ask us for a price" instead of a published figure. Carried on the
       healthcare product model since 27 Sep 2026 and honoured by the update
       controller, but this form never mentioned it, so a pharmacy could not
       set or clear the posture without a direct database write. */
    priceOnRequest: false,
    productType: "PRODUCT",
    requiresPrescription: false,
    ageRestriction: 0,
    controlledSubstance: false,
    countInStock: 0,
    availableSlots: 10,
    image: "",
    /* Optional extra angles — see AdditionalImagesStrip for details. */
    additionalImages: [],
    /* Product videos — frozen contract entries, see ProductVideoSection. */
    videos: [],
    enabled: true,
    measurements: 1,
    requiredPaymentMethod: "NONE",
    discount: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const gate = useProductFormGate(submitting || uploading);
  // Saved photo (https URL) whose X was clicked; opens ProductMediaDeleteDialog.
  const [photoDeleteTarget, setPhotoDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (vendorId) {
      // console.log('Fetching healthcare vendors for vendorId:', vendorId);
      dispatch(fetchHealthcareVendors(vendorId));
      dispatch(getSiUnits());
    }
  }, [dispatch, vendorId]);

  // Fetch sections using healthcareId once vendors are loaded
  useEffect(() => {
    if (healthcareVendors && healthcareVendors.length > 0) {
      const healthcareId = healthcareVendors[0]?.healthcareId;
      if (healthcareId) {
        // console.log('Fetching healthcare sections for healthcareId:', healthcareId);
        dispatch(fetchHealthcareSections(healthcareId));
      }
    }
  }, [dispatch, healthcareVendors]);

  useEffect(() => {
    const mappedUnits = siUnits.map((unit) => ({
      value: unit._id,
      label: unit.unit,
    }));
    setUnits(mappedUnits);
  }, [siUnits]);

  // Load product data once on mount
  useEffect(() => {
    const loadProduct = async () => {
      try {
        const fetchedProduct = await dispatch(fetchHealthcareProduct(productId));
        if (fetchedProduct) {
          const sectionIdToSet = fetchedProduct.sectionId?._id || "";
          setProduct({
            name: fetchedProduct.name || "",
            description: fetchedProduct.description || "",
            section: fetchedProduct.sectionId?._id || "",
            healthcareVendor: fetchedProduct.healthcareId || "",
            price: fetchedProduct.price || 0,
            /* Read back explicitly: without this the form opened every saved
               quote-only product on "Fixed price" and the next save would have
               published a price the pharmacy had deliberately withheld. */
            priceOnRequest: fetchedProduct.priceOnRequest || false,
            productType: fetchedProduct.productType || "PRODUCT",
            requiresPrescription: fetchedProduct.requiresPrescription || false,
            ageRestriction: fetchedProduct.ageRestriction || 0,
            controlledSubstance: fetchedProduct.controlledSubstance || false,
            countInStock: fetchedProduct.countInStock || 0,
            availableSlots: fetchedProduct.availableSlots || 10,
            image: fetchedProduct.image || "",
            additionalImages: Array.isArray(fetchedProduct.additionalImages)
              ? fetchedProduct.additionalImages
              : [],
            videos: Array.isArray(fetchedProduct.videos)
              ? fetchedProduct.videos
              : [],
            enabled: fetchedProduct.enabled !== false,
            measurements: fetchedProduct.measurements || 1,
            requiredPaymentMethod: fetchedProduct.requiredPaymentMethod || "NONE",
            discount: fetchedProduct.discount || null,
          });

          setImagePreview(fetchedProduct.image);

          // Set unit if available - handle both populated object and string ID
          if (fetchedProduct.unit_id) {
            // Check if unit_id is a populated object with _id property
            if (typeof fetchedProduct.unit_id === 'object' && fetchedProduct.unit_id._id) {
              const unitToSet = {
                value: fetchedProduct.unit_id._id,
                label: fetchedProduct.unit_id.unit
              };
              setSelectedUnit(unitToSet);
            }
            // Handle case where unit_id is just a string (ObjectId)
            else if (typeof fetchedProduct.unit_id === 'string') {
              // Find the unit in the units list to get the label
              const matchedUnit = units.find(u => u.value === fetchedProduct.unit_id);
              if (matchedUnit) {
                setSelectedUnit(matchedUnit);
              }
            }
          }

          // Set category if available - handle both populated objects and string IDs
          if (fetchedProduct.category && Array.isArray(fetchedProduct.category)) {
            // Check if categories are populated objects or just IDs
            const processedCategories = fetchedProduct.category.map(cat => {
              // If already a populated object with _id and category properties
              if (typeof cat === 'object' && cat._id && cat.category) {
                return cat;
              }
              // If it's a string ID, try to find it in loaded categories
              else if (typeof cat === 'string') {
                // Note: This will need the categories to be loaded
                // For now, just return it as is and let AsyncSelect handle it
                return cat;
              }
              return cat;
            }).filter(Boolean); // Remove any null/undefined entries

            setSelectedCategory(processedCategories);
          }
        }
      } catch (error) {
        console.error("Error loading product:", error);
        toast.error("Failed to load product details");
      }
    };

    if (productId) {
      loadProduct();
    }
  }, [productId, dispatch]);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Every category, kept for the suggestions to resolve their picks against.
  const [allCategories, setAllCategories] = useState([]);
  const loadCategory = useCallback(async (inputValue) => {
    const res = await fetch("/category/get-all");
    const categories = await res.json();
    if (Array.isArray(categories)) setAllCategories(categories);
    // Grouped, "All"/"Random"/inactive left out (unless already chosen),
    // aliases and keywords searchable.
    return categoryPickerOptions(Array.isArray(categories) ? categories : [], {
      input: inputValue,
      selected: selectedCategory,
    });
  }, [selectedCategory]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProduct({ ...product, [name]: value });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (isMounted.current) {
      setImagePreview(URL.createObjectURL(file));
    }

    try {
      if (isMounted.current) {
        setUploading(true);
      }
      const result = await sharedUpload(file, {
        vendorMedia: {
          vendorType: "healthcare",
          vendorId: product.healthcareVendor || vendorId,
          assetPurpose: "product_cover",
          entityType: "healthcareProduct",
          entityId: productId,
          source: "dashboard",
        },
        onProgress: (percent) => {
          if (isMounted.current) {
            setUploadProgress(percent);
          }
        },
      });

      if (isMounted.current) {
        setProduct({ ...product, image: result.secure_url });
        setUploading(false);
        setUploadProgress(0);
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      if (isMounted.current) {
        setUploading(false);
        setUploadProgress(0);
        toast.error("Image upload failed: " + error.message);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!product.image) {
      toast.error("Please upload a product image");
      return;
    }

    try {
      setSubmitting(true);

      // Process category IDs
      const categoryIds = Array.isArray(selectedCategory)
        ? selectedCategory.map((cat) => cat._id || cat).filter(Boolean)
        : [];

      /* Resolve newly-picked extra angles. The cover image is uploaded
         on file-pick in this form (see handleImageChange — different
         from the other Edit forms which defer until submit), but the
         strip stores dataURLs and uploads on submit. We use the shared
         uploader directly here since this form doesn't expose a
         dataURL→URL helper. */
      let resolvedAdditional = [];
      const picked = Array.isArray(product?.additionalImages)
        ? product.additionalImages
        : [];
      for (const entry of picked) {
        if (typeof entry !== "string" || !entry) continue;
        if (entry.startsWith("data:")) {
          // eslint-disable-next-line no-await-in-loop
          const r = await sharedUpload(entry, {
            skipValidation: true,
            vendorMedia: {
              vendorType: "healthcare",
              vendorId: product.healthcareVendor || vendorId,
              assetPurpose: "product_angle",
              entityType: "healthcareProduct",
              entityId: productId,
              source: "dashboard",
            },
          });
          if (!r?.secure_url) {
            toast.error("One of the extra angles failed to upload.");
            setSubmitting(false);
            return;
          }
          resolvedAdditional.push(r.secure_url);
        } else {
          resolvedAdditional.push(entry);
        }
      }

      const updatedData = {
        name: product.name,
        description: product.description,
        sectionId: product.section,
        healthcareId: product.healthcareVendor,
        /* Zero, not whatever was typed before the vendor switched modes: the
           listing gate in models/shared/tradePricingFields.js lets a product
           go live on the posture alone, and a stale figure travelling with it
           is a price nobody meant to publish. Same expression the brand
           product forms use. */
        price: product.priceOnRequest ? 0 : parseFloat(product.price) || 0,
        priceOnRequest: product.priceOnRequest,
        productType: product.productType,
        requiresPrescription: product.requiresPrescription,
        ageRestriction: parseInt(product.ageRestriction) || 0,
        controlledSubstance: product.controlledSubstance,
        countInStock: product.productType === "PRODUCT" ? parseInt(product.countInStock) : undefined,
        availableSlots: product.productType === "SERVICE" ? parseInt(product.availableSlots) : undefined,
        unit_id: selectedUnit?.value || null,
        measurements: parseFloat(product.measurements) || 1,
        image: product.image,
        additionalImages: resolvedAdditional,
        /* Hydrated on load and replaced wholesale on save — contract-shaped
           entries, uploaded on pick by ProductVideoSection. */
        videos: Array.isArray(product.videos) ? product.videos : [],
        enabled: product.enabled,
        requiredPaymentMethod: product.requiredPaymentMethod,
        discount: discountPayloadOf(product.discount),
        category: categoryIds,
      };

      await dispatch(updateHealthcareProduct(productId, updatedData));
      toast.success("Product/Service updated successfully!");
      history.push("/admin/healthcare-menu");
    } catch (error) {
      console.error("Error updating product:", error);
      if (isMounted.current) {
        toast.error("Failed to update product/service");
      }
    } finally {
      if (isMounted.current) {
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteHealthcareProduct(productId, vendorId));
      toast.success("Product/Service deleted successfully!");
      if (isMounted.current) {
        setShowDeleteModal(false);
      }
      history.push("/admin/healthcare-menu");
    } catch (error) {
      console.error("Error deleting product:", error);
      if (isMounted.current) {
        toast.error("Failed to delete product/service");
      }
    }
  };

  return (
    <Container fluid className="vendor-product-form">
      <Row>
        <Col md="12">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <Card.Title as="h4">Edit Healthcare Product/Service</Card.Title>
                <div className="d-flex" style={{ gap: 8 }}>
                  {AUDIT_ENABLED && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowHistory(true)}
                      title="View every change ever made to this product"
                    >
                      <i className="fa fa-history" style={{ marginRight: 6 }} /> Version History
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete Product
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Vendor team permissions: what this login may change here. */}
                {gate.pricingOnly && (
                  <Alert variant="info" className="mb-3">
                    Your role allows price changes only. Every other field is locked; Save will update the pricing fields.
                  </Alert>
                )}
                {gate.readOnly && (
                  <Alert variant="warning" className="mb-3">
                    {gate.readOnlyMessage}
                  </Alert>
                )}

                <Row>
                  <Col md="6">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>
                        Product/Service Name *
                      </Form.Label>
                      <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        type="text"
                        name="name"
                        value={product.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md="6">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>Type *</Form.Label>
                      <AppSelect block disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        name="productType"
                        value={product.productType}
                        onChange={handleInputChange}
                      >
                        <option value="PRODUCT">Product</option>
                        <option value="SERVICE">Service</option>
                      </AppSelect>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className={classes.label}>Description</Form.Label>
                  <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    as="textarea"
                    rows={3}
                    name="description"
                    value={product.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* Pricing mode. A pharmacy sells the same carton both in bulk and
                    at retail, and for the bulk side it often will not publish a
                    figure at all. The founder decision of 27 Sep 2026 is full
                    commercial parity with brands, so these are the same two tiles
                    as Publications/EditBrandProductForm.js, rebuilt on this form's
                    own Row/Col grid instead of the brand form's flex markup.
                    Without them the vendor could not express the posture the
                    update controller has always accepted. */}
                <Row className="mb-2">
                  {[
                    {
                      val: false,
                      label: "Fixed price",
                      icon: "fas fa-dollar-sign",
                      desc: "Publish a UGX price. Every shopper sees it and can add the item to a cart.",
                    },
                    {
                      val: true,
                      label: "Price on request",
                      icon: "fas fa-comments-dollar",
                      desc: "Hide the price. Shoppers see “Price on request” and contact the pharmacy for a price.",
                    },
                  ].map(({ val, label, icon, desc }) => {
                    const active = product.priceOnRequest === val;
                    return (
                      <Col md="6" key={String(val)} className="mb-2">
                        <div
                          title={gate.pricingTitle}
                          onClick={() =>
                            !gate.lockPricing && setProduct((prev) => ({ ...prev, priceOnRequest: val }))
                          }
                          style={{
                            border: `2px solid ${active ? "#0a578b" : "#e2e8f0"}`,
                            borderRadius: 8,
                            padding: "12px 14px",
                            cursor: "pointer",
                            background: active ? "#eff6ff" : "#fff",
                            transition: "all 0.12s",
                            userSelect: "none",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            height: "100%",
                          }}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 7,
                              flexShrink: 0,
                              background: active ? "#0a578b" : "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i
                              className={icon}
                              style={{
                                fontSize: "0.8rem",
                                color: active ? "#fff" : "#94a3b8",
                              }}
                            ></i>
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: "0.82rem",
                                color: active ? "#0a578b" : "#334155",
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                                marginTop: 2,
                              }}
                            >
                              {desc}
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>

                <Row>
                  {/* Unmounted rather than disabled when the price is on request,
                      so the `required` attribute below cannot block a save for a
                      field the vendor was never shown. */}
                  {!product.priceOnRequest && (
                    <Col md="4">
                      <Form.Group className="mb-3">
                        <Form.Label className={classes.label}>Price (UGX) *</Form.Label>
                        <Form.Control disabled={gate.lockPricing} title={gate.pricingTitle}
                          type="number"
                          name="price"
                          value={product.price}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </Form.Group>
                    </Col>
                  )}

                  <Col md="4">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>
                        {product.productType === "PRODUCT" ? "Stock Quantity" : "Available Slots"}
                      </Form.Label>
                      <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        type="number"
                        name={product.productType === "PRODUCT" ? "countInStock" : "availableSlots"}
                        value={product.productType === "PRODUCT" ? product.countInStock : product.availableSlots}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </Form.Group>
                  </Col>

                  <Col md="4">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>Measurements</Form.Label>
                      <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        type="number"
                        name="measurements"
                        value={product.measurements}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className={classes.label}>Customer payment requirement</Form.Label>
                  <AppSelect block disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    name="requiredPaymentMethod"
                    value={product.requiredPaymentMethod}
                    onChange={handleInputChange}
                  >
                    <option value="NONE">Any method (Cash on Delivery allowed)</option>
                    <option value="ANY_ONLINE">Pay before acceptance (any online method)</option>
                    <option value="MOMO">MTN Mobile Money only</option>
                    <option value="AIRTEL">Airtel Money only</option>
                    <option value="VISA">Card only</option>
                    <option value="ZAMTEL">Zamtel Kwacha only</option>
                  </AppSelect>
                  <Form.Text className="text-muted">
                    Prepaid orders stay locked until Lusuku confirms payment.
                  </Form.Text>
                </Form.Group>
                {/* Vendor discount: the list price above stays; the sale price is derived on read. */}
                <DiscountFields
                  disabled={gate.lockPricing}
                  title={gate.pricingTitle}
                  value={product.discount}
                  onChange={(next) => setProduct((prev) => ({ ...prev, discount: next }))}
                  listPrice={product.price}
                />

                <Card className="mb-3" style={{ backgroundColor: "#FFF3E0", border: "1px solid #FFE0B2" }}>
                  <Card.Body>
                    <h5 className="mb-3">Healthcare Regulations</h5>
                    <Row>
                      <Col md="4">
                        <FormControlLabel
                          control={
                            <Switch disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                              checked={product.requiresPrescription}
                              onChange={(e) =>
                                setProduct({
                                  ...product,
                                  requiresPrescription: e.target.checked,
                                })
                              }
                              color="primary"
                            />
                          }
                          label="Requires Prescription"
                        />
                      </Col>

                      <Col md="4">
                        <FormControlLabel
                          control={
                            <Switch disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                              checked={product.controlledSubstance}
                              onChange={(e) =>
                                setProduct({
                                  ...product,
                                  controlledSubstance: e.target.checked,
                                })
                              }
                              color="secondary"
                            />
                          }
                          label="Controlled Substance"
                        />
                      </Col>

                      <Col md="4">
                        <Form.Group>
                          <Form.Label className={classes.label}>Age Restriction</Form.Label>
                          <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                            type="number"
                            name="ageRestriction"
                            value={product.ageRestriction}
                            onChange={handleInputChange}
                            placeholder="0 (No restriction)"
                            min="0"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Row>
                  <Col md="6">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>Section *</Form.Label>
                      <AppSelect block disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        name="section"
                        value={product.section}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select section</option>
                        {(sections || []).map((section) => (
                          <option key={section._id} value={section._id}>
                            {section.title}
                          </option>
                        ))}
                      </AppSelect>
                    </Form.Group>
                  </Col>

                  <Col md="6">
                    <Form.Group className="mb-3">
                      <Form.Label className={classes.label}>Unit</Form.Label>
                      <Select isDisabled={gate.lockCatalogue}
                        options={unitSelectOptions(siUnits, selectedUnit?.value)}
                        value={selectedUnit}
                        onChange={setSelectedUnit}
                        placeholder="Select unit"
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className={classes.label}>Category *</Form.Label>
                  <AsyncSelect isDisabled={gate.lockCatalogue}
                    cacheOptions
                    defaultOptions
                    isMulti
                    value={selectedCategory}
                    getOptionLabel={(e) => e.category}
                    getOptionValue={(e) => e._id}
                    loadOptions={loadCategory}
                    onChange={(value) => setSelectedCategory(value)}
                    placeholder="Search and select categories"
                  />
                  <CategorySuggestions
                    name={product.name}
                    unitId={selectedUnit?.value || null}
                    vendorId={product.healthcareVendor}
                    categories={allCategories}
                    selected={selectedCategory}
                    disabled={gate.lockCatalogue}
                    onAdd={(category) => setSelectedCategory((prev) => [...(prev || []), category])}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className={classes.label}>Product Image *</Form.Label>
                  {imagePreview && (
                    <div className="mb-2">
                      <img
                        src={imagePreview}
                        alt="Current"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                  )}
                  <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {uploading && (
                    <UploadProgress percent={uploadProgress} label="Uploading image" />
                  )}
                  <MobileProductShootPicker
                    vendorId={vendorId}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    onSelectImage={(imageUrl) => {
                      setProduct((prev) => ({ ...prev, image: imageUrl }));
                      setImagePreview(imageUrl);
                    }}
                  />
                  <AdditionalImagesStrip
                    value={product.additionalImages || []}
                    onChange={(next) =>
                      setProduct((prev) => ({ ...prev, additionalImages: next }))
                    }
                    onRequestDeleteSaved={setPhotoDeleteTarget}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                  />
                </Form.Group>

                <ProductVideoSection
                  videos={product.videos || []}
                  onChange={(next) =>
                    setProduct((prev) => ({ ...prev, videos: next }))
                  }
                  disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                />

                <FormControlLabel
                  control={
                    <Switch disabled={gate.lockActivation} title={gate.activationTitle}
                      checked={product.enabled}
                      onChange={(e) =>
                        setProduct({ ...product, enabled: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="Product/Service Enabled"
                />

                <div className="mt-4 d-flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => history.push("/admin/healthcare-menu")}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={gate.lockSubmit} title={gate.submitTitle}
                    style={{
                      opacity: submitting || uploading ? 0.7 : 1,
                      cursor: submitting || uploading ? 'not-allowed' : 'pointer',
                      backgroundColor: submitting || uploading ? '#6c757d' : '',
                      borderColor: submitting || uploading ? '#6c757d' : '',
                    }}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Updating...
                      </>
                    ) : uploading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Uploading Image...
                      </>
                    ) : (
                      "Update Product/Service"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "<strong>{product.name}</strong>"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Product
          </Button>
        </Modal.Footer>
      </Modal>

      {AUDIT_ENABLED && (
        <VersionHistoryDrawer
          show={showHistory}
          onHide={() => setShowHistory(false)}
          entityType="HealthcareProduct"
          entityId={productId}
          entityLabel={product?.name}
        />
      )}

      <ProductMediaDeleteDialog
        target={photoDeleteTarget}
        productType="healthcare"
        productId={productId}
        productName={product?.name}
        image={product?.image}
        additionalImages={product?.additionalImages}
        onClose={() => setPhotoDeleteTarget(null)}
        onRemoved={({ image, additionalImages }) =>
          setProduct((prev) => ({ ...prev, image, additionalImages }))
        }
      />
    </Container>
  );
};

export default EditHealthcareProductForm;
